// Shared cross-dataset set-reference resolver.
//
// A `:where` / `filter` predicate may reference another bound dataset's
// column to filter by the ids it produced —
// `(in externalId (col other-ds externalId))`. `(col …)` produces a
// `ColumnRef` Value with no `compileExpr`, so left as-is it can be
// neither pushed to CDF (the value side of a pushable leaf must be a
// literal) nor evaluated client-side by the `filter` verb (it throws
// `NotAnExpressionOpError`). This module resolves + inlines the value
// list up front so the intuitive cross-dataset pattern Just Works for
// BOTH consumers:
//
//   - `query-knowledge-graph` (`ops/knowledge-graph/qkg.ts`) calls it
//     eagerly before `partitionFilter`; the inlined literal vector
//     pushes to CDF as an `in` filter (or, for a direct-relation
//     subpath, a DMS `nested` filter — see `pushdown.ts`).
//   - the s-expr `transform` tool's client-side `filter` verb calls it
//     at plan-replay / materialize time, just before `compileExpr`, so
//     a lazily-threaded filter can still reference another dataset
//     without forcing materialisation up front.
//
// Eager qkg resolution literalises the residual predicate, so the
// `filter`-verb replay seam is a genuine no-op for an already-resolved
// qkg residual (no `(col …)` left to find → the walk returns the node
// unchanged).
//
// Predicate ASTs only use positional `params` (boolean combinators +
// comparison/`in` leaves + `(col …)`), so the walk recurses `params`
// and vector elements; kwargs are not part of a predicate tree.

import { SET_REF_MAX } from '#core/lib/cognite/view.ts';

import type { SourcePos } from './ast.ts';
import { projectColumnRef } from './column-ref.ts';
import type { EvalResult } from './env.ts';
import { EmptySetRefError, SetRefTooLargeError } from './errors.ts';
import { type AstInput, astShape } from './normalize.ts';
import type { Session } from './session/Session.ts';
import { expectColumnRef, type LazyDataset, type MaterializedDataset } from './value.ts';

/**
 * Narrow capability bundle the resolver needs — deliberately NOT a
 * qkg `SpecialCtx`, so the s-expr `filter` verb (which only has a
 * `SpecialVerbCtx`) can call it too. `evalNode` is pre-bound to the
 * caller's `env`/`path`; `pos` is the resolved-error source position.
 *
 * `skipNode` (optional) is a structural discriminator the caller uses
 * to exempt a `(col …)` node from cross-dataset resolution. The qkg
 * caller passes `isRelationSubpathRef(node, viewSchema)` so a
 * queried-view relation-subpath LHS — `(col <view> <relCol>
 * externalId)`, whose first arg is the view, not a session binding —
 * is left untouched for push-down (`partitionFilter` /
 * `relationSubpathLeaf` / `translateLeaf`) instead of being
 * mis-resolved as a binding (a spurious `UnboundSymbolError`). The
 * client-side `filter`-verb caller passes a discriminator that skips
 * any `(col …)` whose first label is NOT a session binding — that is
 * a per-row struct-sub-path accessor on the current row (resolved by
 * `compileExpr`), not a cross-dataset set reference; only a `(col
 * <bound-ds> …)` is a genuine set-ref for `(in …)`.
 */
export type SetRefResolverDeps = {
  readonly evalNode: (node: AstInput) => Promise<EvalResult>;
  readonly session: Session;
  readonly materialize: (ds: LazyDataset) => Promise<MaterializedDataset>;
  readonly pos: SourcePos;
  readonly skipNode?: (node: AstInput) => boolean;
};

/**
 * Resolve every `(col other-ds column [path…])` reference inside a
 * predicate AST to an inline literal vector of that column's
 * materialised values, returning a rewritten predicate the standard
 * push-down / residual / client-side paths can consume.
 *
 * The referenced binding is materialised eagerly here — memoised
 * through `session.fetch`, so a binding produced by an earlier
 * conversation turn is not re-fetched. Resolved values are
 * structurally de-duplicated and capped at `SET_REF_MAX`
 * (`SetRefTooLargeError`) to stay within CDF's instance-filter size
 * limit; a resolved set of zero values is a hard `EmptySetRefError`
 * (an empty pushed `in` matches nothing, almost always an upstream
 * mistake rather than an intentional empty filter).
 *
 * Operator forgiveness: when an `=` / `!=` has exactly one operand
 * that is a foreign `(col other-ds …)` set-ref (the other operand a
 * row expression), the comparison is rewritten to set membership /
 * non-membership — `(= x (col other c))` ⇒ `(in x <set>)`,
 * `(!= …)` ⇒ `(not (in …))`. This matches the intuitive
 * cross-dataset filter (`(= (col self k) (col other k))`, where the
 * self side is `skipNode`-exempt and stays the row operand). The
 * canonical `(in row (col other c))` is unchanged. Applies on every
 * path that shares this resolver (transform client-side filter AND
 * qkg `:where` push-down — an `in` leaf is equally pushable).
 */
export const resolveSetRefsInPredicate = async (node: AstInput, deps: SetRefResolverDeps): Promise<AstInput> => {
  const shape = astShape(node);

  if (shape.kind === 'opCall' && shape.call.name === 'col') {
    // A caller-supplied structural discriminator can exempt this
    // `(col …)` from cross-dataset resolution. qkg passes
    // `isRelationSubpathRef` so a queried-view relation-subpath LHS
    // `(col <view> <relCol> externalId)` is left for push-down rather
    // than mis-resolved as a (non-existent) binding. Leaving it
    // untouched is correct: `partitionFilter` recognises it again via
    // `relationSubpathLeaf` and folds it to a DMS `nested` filter; the
    // sibling cross-dataset RHS is still resolved by the recursion below.
    if (deps.skipNode?.(node) === true) {
      return node;
    }

    // Reuse `col`'s own apply for Env→Session resolution + schema
    // validation (surfaces UnboundSymbolError / UnknownColumnRefError
    // with proper diagnostics), then project + materialise the column.
    const { value } = await deps.evalNode(node);
    const ref = expectColumnRef(value, '(col …) inside a predicate', deps.pos);
    // SSOT: resolve → materialise-if-lazy → per-cell `ref.path` walk.
    // `ref.path` is already schema-validated at `col` apply time, so
    // the per-cell walk is plain (safe) struct access.
    const { cells: walked } = await projectColumnRef(ref, deps.session, deps.pos, deps.materialize);
    const unique = Array.from(new Map(walked.map((v) => [JSON.stringify(v), v] as const)).values());
    if (unique.length === 0) {
      throw new EmptySetRefError(deps.pos);
    }
    if (unique.length > SET_REF_MAX) {
      throw new SetRefTooLargeError(unique.length, SET_REF_MAX, deps.pos);
    }
    // The value list is a literal vector AstInput (primitives /
    // `{space, externalId}` structs for a direct-relation subpath).
    return unique as AstInput;
  }

  if (shape.kind === 'opCall') {
    const call = shape.call;
    const params = call.params ?? [];
    const resolvedParams = await Promise.all(params.map((p) => resolveSetRefsInPredicate(p, deps)));
    // `=` / `!=` with exactly ONE operand a *foreign* `(col other-ds
    // …)` set-ref ⇒ set membership / non-membership (SQL `= ANY`),
    // matching the intuitive cross-dataset filter the LLM writes
    // (`(= (col self k) (col other k))`). The canonical `(in row (col
    // other col))` is unaffected — `in`'s 2nd operand is already a
    // vector here and `in` does membership. A self-reference is
    // `skipNode`-exempt upstream, so it is NOT a foreign operand: it
    // stays the row side, yielding `(in (col self k) <other-set>)`.
    if ((call.name === '=' || call.name === '!=') && params.length === 2) {
      const isForeignColRef = (p: AstInput): boolean => {
        const s = astShape(p);
        return s.kind === 'opCall' && s.call.name === 'col' && deps.skipNode?.(p) !== true;
      };
      const lhsSet = isForeignColRef(params[0] as AstInput);
      const rhsSet = isForeignColRef(params[1] as AstInput);
      if (lhsSet !== rhsSet) {
        const setIdx = lhsSet ? 0 : 1;
        const membership = {
          name: 'in',
          params: [resolvedParams[setIdx === 0 ? 1 : 0], resolvedParams[setIdx]],
        };
        return (call.name === '=' ? membership : { name: 'not', params: [membership] }) as AstInput;
      }
    }
    return { ...call, params: resolvedParams };
  }

  if (shape.kind === 'vector') {
    return Promise.all(shape.elements.map((el) => resolveSetRefsInPredicate(el, deps)));
  }

  return node;
};
